import { create } from 'zustand';

/**
 * Where the Creator Agreement gate was opened from. Travels on every
 * creator_contract_* event — without it, first-login auto-opens are
 * indistinguishable from creators bouncing off an upload attempt.
 */
export type ContractGateSource =
  | 'first_login'
  | 'create_menu_upload'
  | 'create_menu_post'
  | 'create_menu_course'
  | 'create_menu_series'
  | 'studio_upload'
  | 'studio_resume'
  | 'studio_visibility'
  | 'studio_codes'
  | 'community_post'
  | 'unknown';

/**
 * Transient gate state for the Creator Agreement dialog.
 *
 * Unsigned creators see the dialog only when attempting a creator action.
 * `pendingAction` captures the work to run after acceptance so the user
 * doesn't lose context (e.g., the upload modal opens automatically).
 */
interface ContractGateState {
  isOpen: boolean;
  pendingAction: (() => void) | null;
  source: ContractGateSource | null;
  /** Epoch ms captured when the gate opened — powers `seconds_open` on events. */
  openedAt: number | null;
  open: (action?: () => void, source?: ContractGateSource) => void;
  confirmClose: () => void;
  runPendingAndClose: () => void;
}

const CLOSED = { isOpen: false, pendingAction: null, source: null, openedAt: null } as const;

export const useContractGateStore = create<ContractGateState>((set, get) => ({
  ...CLOSED,
  open: (action, source) =>
    set({
      isOpen: true,
      pendingAction: action ?? null,
      source: source ?? 'unknown',
      openedAt: Date.now(),
    }),
  confirmClose: () => set(CLOSED),
  runPendingAndClose: () => {
    const { pendingAction } = get();
    set(CLOSED);
    if (pendingAction) pendingAction();
  },
}));
